import { useEffect, useState } from "react";
import { type as text } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { api } from "../api/client";
import { useExperience } from "../context/ExperienceContext";
import { buildInsightsMap } from "../components/ControlInsightsPanel";
import type { ControlInsights } from "../components/ControlInsightsPanel";
import type { FrameworkId } from "../context/FrameworksContext";
import { FrameworkControlList } from "../components/FrameworkControlList";
import type { SoaRow, EvidenceDoc } from "../components/FrameworkControlList";
import { StandardHeader, StandardNote, STANDARD_PAGE_LABELS } from "../components/standards/StandardHeader";

// IASME Cyber Assurance v7.0, one page per size tier (four FrameworkIds, one component). The 14
// themes sit on the shared assessment list; a status is recorded without deciding applicability
// (applicable: null), as the scheme assesses every theme at the tier's depth.

const TIER_META: Record<string, { label: string; headcount: string; scope: string }> = {
  iasme_ca_sole_trader: {
    label: "Sole trader",
    headcount: "1 to 2 people",
    scope: "Themes 1, 8, and 14 are mandatory for all sizes. For sole traders a lightweight implementation is acceptable; the assessment is proportionate to the simplicity of the organisation.",
  },
  iasme_ca_micro: {
    label: "Micro business",
    headcount: "3 to 9 people",
    scope: "All 14 themes apply. Assessors expect proportionate documentation suited to a micro business (approximately 20 requirements assessed).",
  },
  iasme_ca_small: {
    label: "Small business",
    headcount: "10 to 49 people",
    scope: "All 14 themes apply with increasing depth. Documented policies and operational evidence are expected (approximately 32 requirements assessed).",
  },
  iasme_ca_medium_large: {
    label: "Medium or large organisation",
    headcount: "50 or more people",
    scope: "All 14 themes at full depth: all 65 requirements are assessed. A structured information security programme is expected.",
  },
};

export function IasmeCaAssessmentPage({ frameworkId }: { frameworkId: FrameworkId }) {
  const { novice } = useExperience();
  const [rows, setRows] = useState<SoaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [insightsMap, setInsightsMap] = useState<Map<string, ControlInsights>>(new Map());
  const [docs, setDocs] = useState<EvidenceDoc[]>([]);

  const meta = TIER_META[frameworkId];

  async function loadAll() {
    const [{ soa }, latestRun, docsData] = await Promise.all([
      api.get<{ soa: SoaRow[] }>("/controls/soa"),
      api.get<{ run: { resultJson: unknown } | null }>("/guidance/latest").catch(() => ({ run: null })),
      api.get<{ documents: EvidenceDoc[] }>("/documents").catch(() => ({ documents: [] as EvidenceDoc[] })),
    ]);
    setRows(soa.filter((r) => r.control.frameworkId === frameworkId));
    if (latestRun.run?.resultJson) setInsightsMap(buildInsightsMap(latestRun.run.resultJson));
    setDocs(docsData.documents);
  }

  useEffect(() => {
    setLoading(true);
    loadAll().catch(() => {}).finally(() => setLoading(false));
  }, [frameworkId]);

  if (loading) return <LoadingBlock />;

  return (
    <div className="page-pad">
      <StandardHeader
        frameworkId={frameworkId}
        page={STANDARD_PAGE_LABELS.assessment}
        subtitle={`${meta.label} tier (${meta.headcount}): assess each of the 14 themes and attach the evidence the assessor will look for.`}
      >
        <p style={{ ...text.hint, margin: "6px 0 0", maxWidth: 820 }}>{meta.scope}</p>
      </StandardHeader>

      <FrameworkControlList
        rows={rows}
        docs={docs}
        frameworkId={frameworkId}
        loadAll={loadAll}
        novice={novice}
        insightsMap={insightsMap}
        groups={[]}
        noun="theme"
        statusLeavesApplicabilityOpen
      />

      <StandardNote title="IASME Cyber Assurance certification">
        Once all themes show <strong>Compliant</strong>, contact your IASME-approved certification body to arrange
        an assessment. An assessor will review your evidence and documentation against the v7.0 standard for your
        size tier. Certification is valid for one year and must be renewed annually.
      </StandardNote>
    </div>
  );
}
